import { useEffect, useState } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import { Lock, Loader2, CheckCircle2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Logo from "@/components/Logo";
import { resetPassword, formatApiError } from "@/lib/api";

/**
 * Public reset-password landing page.
 *
 * The token is read from the ?token=... query string. We deliberately do
 * NOT validate the token client-side — the backend is the source of
 * truth. We just relay the user's chosen password and surface whatever
 * the backend says (success → bounce to /login, failure → show the
 * error inline so the user can try again).
 */
export default function ResetPassword() {
    const [params] = useSearchParams();
    const navigate = useNavigate();
    const token = params.get("token") || "";

    const [pw, setPw] = useState("");
    const [pw2, setPw2] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [done, setDone] = useState(false);

    useEffect(() => {
        if (!token) {
            toast.error("This reset link is missing its token.");
        }
    }, [token]);

    const tooShort = pw.length > 0 && pw.length < 8;
    const mismatch = pw2.length > 0 && pw !== pw2;
    const canSubmit = !!token && pw.length >= 8 && pw === pw2 && !submitting;

    const submit = async (e) => {
        e.preventDefault();
        if (!canSubmit) return;
        setSubmitting(true);
        try {
            await resetPassword({ token, new_password: pw });
            setDone(true);
            toast.success("Password updated.");
            setTimeout(() => navigate("/login", { replace: true }), 2200);
        } catch (err) {
            toast.error(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-16 paper-texture" data-testid="reset-page">
            <div className="w-full max-w-md">
                <div className="flex items-center gap-3 mb-8 justify-center">
                    <Logo variant="icon" size={56} />
                    <div>
                        <div className="font-serif-display text-3xl text-[var(--vm-text)] leading-none lowercase tracking-tight">rooteed</div>
                        <div className="text-xs uppercase tracking-[0.2em] text-[var(--vm-sage)] mt-1">Garden Journal</div>
                    </div>
                </div>

                <div className="journal-card p-8">
                    {!token ? (
                        <div className="text-center" data-testid="reset-no-token">
                            <div className="w-12 h-12 rounded-full bg-[var(--vm-secondary)]/15 flex items-center justify-center mx-auto mb-4">
                                <AlertCircle className="w-6 h-6 text-[var(--vm-secondary)]" strokeWidth={1.6} />
                            </div>
                            <h1 className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">
                                This reset link looks broken
                            </h1>
                            <p className="text-sm text-[var(--vm-text-secondary)]">
                                Open the link directly from the email you received, or request a new one.
                            </p>
                            <Link to="/forgot-password" className="block mt-5">
                                <Button data-testid="reset-request-new"
                                    className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]">
                                    Request a new link
                                </Button>
                            </Link>
                        </div>
                    ) : done ? (
                        <div className="text-center" data-testid="reset-confirmation">
                            <div className="w-12 h-12 rounded-full bg-[var(--vm-sage)]/15 flex items-center justify-center mx-auto mb-4">
                                <CheckCircle2 className="w-6 h-6 text-[var(--vm-sage)]" strokeWidth={1.6} />
                            </div>
                            <h1 className="font-serif-display text-2xl text-[var(--vm-text)] mb-2">
                                Password updated
                            </h1>
                            <p className="text-sm text-[var(--vm-text-secondary)]">
                                Taking you to the sign-in page…
                            </p>
                        </div>
                    ) : (
                        <>
                            <h1 className="font-serif-display text-3xl text-[var(--vm-text)] mb-2">
                                Set a new password
                            </h1>
                            <p className="text-sm text-[var(--vm-text-muted)] mb-6">
                                Pick something at least 8 characters long.
                            </p>
                            <form onSubmit={submit} className="space-y-4">
                                <div>
                                    <Label htmlFor="reset-pw" className="text-xs uppercase tracking-wide text-[var(--vm-sage)]">New password</Label>
                                    <div className="relative mt-1">
                                        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vm-text-muted)]" />
                                        <Input
                                            id="reset-pw"
                                            type="password"
                                            data-testid="reset-password-input"
                                            value={pw}
                                            onChange={(e) => setPw(e.target.value)}
                                            placeholder="At least 8 characters"
                                            required
                                            autoFocus
                                            className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                        />
                                    </div>
                                    {tooShort && (
                                        <div className="text-xs text-[var(--vm-secondary)] mt-1">
                                            Add at least {8 - pw.length} more character{8 - pw.length === 1 ? "" : "s"}.
                                        </div>
                                    )}
                                </div>
                                <div>
                                    <Label htmlFor="reset-pw2" className="text-xs uppercase tracking-wide text-[var(--vm-sage)]">Confirm</Label>
                                    <div className="relative mt-1">
                                        <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--vm-text-muted)]" />
                                        <Input
                                            id="reset-pw2"
                                            type="password"
                                            data-testid="reset-password-confirm"
                                            value={pw2}
                                            onChange={(e) => setPw2(e.target.value)}
                                            placeholder="Type it again"
                                            required
                                            className="pl-9 bg-[var(--vm-bg)] border-[var(--vm-border)]"
                                        />
                                    </div>
                                    {mismatch && (
                                        <div className="text-xs text-[var(--vm-secondary)] mt-1">Passwords don't match.</div>
                                    )}
                                </div>
                                <Button
                                    type="submit"
                                    disabled={!canSubmit}
                                    data-testid="reset-submit-btn"
                                    className="w-full rounded-full bg-[var(--vm-accent)] hover:bg-[var(--vm-accent-hover)] text-[var(--vm-bg)]"
                                >
                                    {submitting
                                        ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Updating…</>)
                                        : "Update password"}
                                </Button>
                            </form>
                        </>
                    )}
                </div>
            </div>
        </div>
    );
}
